/**
 * Générateur — Attendu 199 — O9 « Il utilise simultanément les boucles
 * "Répéter … fois" et "Répéter jusqu'à …" ainsi que les instructions
 * conditionnelles pour réaliser des figures, des programmes de calculs, des
 * déplacements, des simulations d'expérience aléatoire. »
 * Niveau : 4ème | Thème #12 Algorithmique (O9, Niveau 3) | Session B.
 * (Attendu 199 = remap manuel du parse — fuzzy cassé par un wrap.)
 * Source prof : O9 MUET en texte ; critère canonique = l'image page 2
 * (simulation « 100 nombres aléatoires 0/1, compter les 0 et les 1 »).
 * → BANQUE.
 *
 * ADAPTATIONS scoring : la simulation aléatoire → on DONNE le résultat observé
 * (c fois « 1 » sur T lancers) et on demande la FRÉQUENCE (calculable), pas une
 * exécution non déterministe. « répéter jusqu'à » → NEUF svgScratch (cadre en C
 * à condition, additif).
 *
 * 4 cartes :
 *  1. jusqua_iterations — combien de répétitions de « répéter jusqu'à » ? (saisie).
 *  2. simulation_freq   — fréquence d'apparition du « 1 » (saisie, %).
 *  3. simulation_qcm    — quelle expérience aléatoire ce script simule-t-il ? (QCM).
 *  4. deux_boucles      — « répéter n fois » vs « répéter jusqu'à » (QCM).
 */

import {
    randInt, randomChoice, makeQcm, makeSaisie,
} from '../../../utils';
import { svgScript, imgElement } from './algo';

const ATTENDU_ID = 199;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_JUSQUA = 'Répéter jusqu\'à : combien de répétitions ?';
const L_FREQ = 'Simulation : la fréquence du « 1 »';
const L_SIMU = 'Simulation : quelle expérience ?';
const L_DEUX = 'Répéter n fois ou répéter jusqu\'à ?';

// ═════════ Carte 1 — répéter jusqu'à : itérations (saisie) ═════════

const genJusquaIterations = () => {
    const x0 = randInt(0, 5);
    const p = randInt(2, 5);
    const k = randInt(3, 9);                 // nombre de répétitions voulu
    const seuil = x0 + (k - 1) * p + randInt(1, p);   // x0+k·p > seuil ≥ x0+(k-1)·p+1
    // vérif : plus petit k' tel que x0 + k'·p > seuil
    const kReel = Math.floor((seuil - x0) / p) + 1;
    const script = svgScript([
        { t: 'hat', txt: 'quand le drapeau est cliqué' },
        { t: 'variable', txt: `mettre x à ${x0}` },
        { t: 'repeat', jusqua: `x > ${seuil}`, body: [
            { t: 'variable', txt: `ajouter ${p} à x` },
        ] },
    ]);
    const texte = `<p><b>Le lutin exécute ce script. La variable x part de ${x0}. `
        + 'Combien de fois le bloc « ajouter » est-il exécuté (combien de répétitions) ?</b></p>';
    const payload = makeSaisie(String(kReel), texte,
        { ...opts(L_JUSQUA), elements_additionnels: imgElement(script) });
    payload._meta.params = { x0, p, seuil, kReel };
    return payload;
};

// ═════════ Carte 2 — fréquence du « 1 » (saisie, %) ═════════

const genSimulationFreq = () => {
    const T = randomChoice([20, 25, 50]);
    const c = randInt(1, T - 1);
    const freqPct = (c * 100) / T;   // entier par construction (T divise 100·c)
    const texte = `<p><b>Un script tire ${T} fois un nombre aléatoire égal à 0 ou à 1, et compte `
        + `les « 1 ». On obtient ${c} fois « 1 ».</b></p>`
        + '<p>Quelle est la fréquence d\'apparition du « 1 », en pourcentage ?</p>';
    const payload = makeSaisie(String(freqPct), texte,
        { ...opts(L_FREQ), suffixe: '%' });
    payload._meta.params = { T, c, freqPct };
    return payload;
};

// ═════════ Carte 3 — quelle expérience ? (QCM) ═════════

const PRENOMS = ['Léna', 'Youri', 'Sacha', 'Nina', 'Timéo', 'Awa', 'Malo', 'Jade'];

const genSimulationQcm = () => {
    const T = randomChoice([50, 100, 200]);
    const prenom = randomChoice(PRENOMS);
    const correct = 'Un lancer de pièce (pile ou face) : deux issues également probables.';
    const choix = [
        correct,
        'Un lancer de dé à six faces.',
        'Un tirage d\'une carte dans un jeu de 52.',
        'Une roue de loterie à dix secteurs.',
    ];
    const texte = `<p><b>Le script de ${prenom} tire ${T} fois un nombre aléatoire égal à 0 ou à 1. `
        + 'Quelle expérience aléatoire ce script simule-t-il ?</b></p>';
    const payload = makeQcm(choix, correct, texte, opts(L_SIMU));
    payload._meta.params = { T, prenom };
    return payload;
};

// ═════════ Carte 4 — répéter n fois vs jusqu'à (QCM) ═════════

const genDeuxBoucles = () => {
    const n = randInt(3, 8);
    const seuil = randInt(10, 30);
    const correct = '« répéter n fois » s\'exécute un nombre connu de fois ; « répéter jusqu\'à » '
        + 's\'arrête dès qu\'une condition devient vraie.';
    const choix = [
        correct,
        'Les deux boucles font exactement la même chose.',
        '« répéter jusqu\'à » s\'exécute toujours une seule fois.',
        '« répéter n fois » ne s\'arrête jamais.',
    ];
    const texte = `<p><b>On compare « répéter ${n} fois » et « répéter jusqu'à (x > ${seuil}) ». `
        + 'Quelle est la différence ?</b></p>';
    const payload = makeQcm(choix, correct, texte, opts(L_DEUX));
    payload._meta.params = { n, seuil };
    return payload;
};

// ═════════ Assemblage ═════════

const GENERATORS = {
    jusqua_iterations_texte_saisie: genJusquaIterations,
    simulation_freq_texte_saisie: genSimulationFreq,
    simulation_qcm_unique: genSimulationQcm,
    deux_boucles_qcm_unique: genDeuxBoucles,
};

const CARTES = [
    { id: 'jusqua_iterations', label: L_JUSQUA },
    { id: 'simulation_freq', label: L_FREQ },
    { id: 'simulation_qcm', label: L_SIMU },
    { id: 'deux_boucles', label: L_DEUX },
];

export const CATEGORIES = CARTES.map((c) => ({
    id: c.id,
    label: c.label,
    formatsList: Object.keys(GENERATORS).filter((k) => k.startsWith(`${c.id}_`))
        .map((k) => ({ key: k, label: c.label })),
}));

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
